/**
 * OAuth callback handler.
 * After Google/Microsoft auth, Supabase redirects here with tokens in the URL hash.
 * We explicitly exchange the code/tokens, store the session, then redirect.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Zap } from "lucide-react";
import { supabase } from "../lib/supabase-client.ts";
import { consumePostAuthRedirect, setAccessToken } from "../lib/api.ts";

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  /** Both session branches (getSession hash-parse and PKCE code exchange) finish identically.
   *  The redirect resolution handles the magic-link fresh tab: no sessionStorage redirect
   *  there, so it falls back to the wizard when a checkup stash is waiting — see
   *  consumePostAuthRedirect in api.ts. */
  function finishSignIn(accessToken: string) {
    setAccessToken(accessToken);
    const redirect = consumePostAuthRedirect();
    // Give the auth context a beat to process SIGNED_IN before navigating.
    setTimeout(() => navigate(redirect, { replace: true }), 500);
  }

  useEffect(() => {
    async function handleCallback() {
      try {
        // Check for error in URL params (Supabase sends errors as query params)
        const params = new URLSearchParams(window.location.search);
        const errorParam = params.get("error_description") || params.get("error");
        if (errorParam) {
          setError(decodeURIComponent(errorParam));
          return;
        }

        // Try to get the session — Supabase client auto-parses the URL hash/code
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          setError(sessionError.message);
          return;
        }

        if (session) {
          finishSignIn(session.access_token);
          return;
        }

        // No session from getSession — try exchanging code from URL if present
        // (PKCE flow puts code in query params, not hash)
        const code = params.get("code");
        if (code) {
          const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            setError(exchangeError.message);
            return;
          }
          if (data.session) {
            finishSignIn(data.session.access_token);
            return;
          }
        }

        // Nothing worked — redirect to login
        setError("Could not complete sign-in. Please try again.");
      } catch (e: any) {
        setError(e.message || "Authentication failed");
      }
    }

    handleCallback();
  }, [navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-surface-secondary flex items-center justify-center p-4">
        <div className="text-center max-w-sm">
          <Zap size={32} className="text-danger mx-auto mb-3" />
          <p className="text-[16px] text-danger font-medium mb-2">Sign-in failed</p>
          <p className="text-[14px] text-text-secondary mb-4">{error}</p>
          <button
            onClick={() => navigate("/", { replace: true })}
            className="px-4 py-2 bg-brand text-on-solid rounded-lg text-[14px] font-medium hover:bg-brand/90"
          >
            Back to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-secondary flex items-center justify-center">
      <div className="text-center">
        <Zap size={32} className="text-brand mx-auto mb-3 animate-pulse" />
        <p className="text-[16px] text-text-secondary">Signing you in...</p>
      </div>
    </div>
  );
}
